'use client';

import { Box, Stack, Typography } from '@mui/material';
import { TreasuryQuoteChart } from './TreasuryQuoteChart';
import { formatTreasuryDate, seriesStats, type TreasuryQuotePoint } from './treasury-view';

type Props = {
  tickerName: string | null;
  titleType: string;
  maturityDate: string;
  quoteDate: string;
  sourceAsOf: string;
  points: TreasuryQuotePoint[];
};

const numberFormat = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
const currencyFormat = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const format = (value: number | null, currency = false) =>
  value === null ? '—' : (currency ? currencyFormat : numberFormat).format(value);

export function TreasuryQuoteTabClient({ tickerName, titleType, maturityDate, quoteDate, sourceAsOf, points }: Props) {
  const stats = seriesStats(points, 'sellRate');
  return (
    <Stack component='section' aria-labelledby='tesouro-historico' spacing={{ xs: 2, md: 2.5 }}>
      <Box>
        <Typography id='tesouro-historico' component='h2' variant='h4' sx={{ fontWeight: 800 }}>
          Cotação e histórico
        </Typography>
        <Typography color='text.secondary' sx={{ mt: 0.5 }}>
          {tickerName || titleType} · vencimento em {formatTreasuryDate(maturityDate)}
        </Typography>
      </Box>
      <TreasuryQuoteChart
        title='Taxas e preços unitários de compra e venda'
        points={points}
        series={[
          { key: 'sellRate', label: 'Taxa de venda (a.a.)', axis: 'left' },
          { key: 'sellPu', label: 'Preço unitário de venda', axis: 'right' },
          { key: 'buyRate', label: 'Taxa de compra (a.a.)', axis: 'left' },
          { key: 'buyPu', label: 'Preço unitário de compra', axis: 'right' },
        ]}
        defaultEnabled={['sellRate', 'sellPu']}
        tooltipSeries={['buyRate', 'sellRate', 'buyPu', 'sellPu']}
        statsSeries='sellRate'
      />
      {stats ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 3 }}>
          <Stat label='Mínimo · venda' value={`${format(stats.min)}%`} />
          <Stat label='Máximo · venda' value={`${format(stats.max)}%`} />
          <Stat label='Média · venda' value={`${format(stats.average)}%`} />
          <Stat label='Variação · venda' value={`${stats.change >= 0 ? '+' : ''}${format(stats.change)} p.p.`} />
        </Stack>
      ) : null}
      <Typography variant='caption' color='text.secondary'>
        Fonte: Tesouro Transparente · última cotação em {formatTreasuryDate(quoteDate)} · atualização da fonte em{' '}
        {formatTreasuryDate(sourceAsOf)}. A variação é da série de venda publicada, sem representar retorno realizado.
      </Typography>
    </Stack>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ flex: 1 }}>
      <Typography variant='caption' color='text.secondary'>
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 700 }}>{value}</Typography>
    </Box>
  );
}
